import Auth from '../Auth';
import Storage from '../Util/Storage';
import { getEnvConfigByKey } from '../config/EnvConfig';
import { useEffect } from 'react';
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  MutationOptions,
  OperationVariables,
  QueryOptions,
  useApolloClient,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { AuthOptions, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

export const NOTIFICATION_REQUEST: string = 'notification-graphql';
export const BACKEND_REQUEST: string = 'backend';

interface CreateInspireAuthLinkProps {
  httpLink: ApolloLink;
  region: string;
  url: string;
  reqName: string;
}

class AppSyncClient {
  private constructor() {}

  private static client: ApolloClient | null = null;

  static getClient(): ApolloClient {
    if (this.client) return this.client;
    return this.createClient();
  }

  private static createClient() {
    const config = this.getConfig();

    const thunderHttpLink = createHttpLink({
      uri: config.thunderUrl,
      includeUnusedVariables: true,
    });
    const thunderLink = this.createInspireLinkWithAuth({
      httpLink: thunderHttpLink,
      url: config.thunderUrl!,
      region: config.region,
      reqName: BACKEND_REQUEST,
    });

    let appLink: ApolloLink = thunderLink;

    if (config.isNotificationEnabled && config.notificationUrl) {
      const notificationHttpLink = createHttpLink({
        uri: config.notificationUrl,
        includeUnusedVariables: true,
      });

      const notificationLink = this.createInspireLinkWithAuth({
        httpLink: notificationHttpLink,
        url: config.notificationUrl,
        region: config.notificationRegion,
        reqName: NOTIFICATION_REQUEST,
      });

      appLink = ApolloLink.split(
        operation => operation.getContext().clientName === NOTIFICATION_REQUEST,
        notificationLink,
        thunderLink
      );
    }

    this.client = new ApolloClient({
      link: appLink,
      cache: this.createCache(),
      defaultOptions: {
        // keeping existing behaviour to prevent regression
        query: {
          fetchPolicy: 'network-only',
        },
        // keeping existing behaviour to prevent regression
        watchQuery: {
          fetchPolicy: 'network-only',
        },
      },
    });

    return this.client;
  }

  private static createInspireLinkWithAuth({
    httpLink,
    region,
    url,
    reqName,
  }: CreateInspireAuthLinkProps) {
    const auth: AuthOptions = {
      type: 'AWS_IAM',
      async credentials() {
        const { accessKeyId, secretAccessKey, sessionToken } =
          await Auth.currentAccess(reqName);
        return {
          accessKeyId: accessKeyId || '',
          secretAccessKey: secretAccessKey || '',
          sessionToken: sessionToken || '',
        };
      },
    };

    return ApolloLink.from([
      setContext((request, previousContext) => {
        return {
          headers: {
            ...previousContext.headers,
            ...{
              'X-RP-TOKEN': Storage.getItem('rpToken'),
              'X-SESSION-ID': Storage.getItem('sessionID'),
              ...(Storage.getItem('requesterOwner')
                ? { 'X-GO-OWNER': Storage.getItem('requesterOwner') }
                : {}),
            },
            ...(Auth.isImpersonating() && {
              'IMPERSONATION-SUBJECT': Storage.getItem('imprSubject'),
              'IMPERSONATION-USER-ID': Storage.getItem('imprUser'),
            }),
          },
        };
      }),
      createAuthLink({
        url,
        region,
        auth,
      }),
      createSubscriptionHandshakeLink(
        {
          url,
          region,
          auth,
        },
        httpLink
      ),
    ]);
  }

  private static createCache() {
    return new InMemoryCache({
      dataIdFromObject: (object: any) => {
        switch (object.__typename) {
          case 'WeeklySummary':
            return object.uuid;
          default:
            return object.id ?? object._id;
        }
      },
    });
  }

  private static getConfig() {
    return {
      thunderUrl:
        getEnvConfigByKey('graphQLEndpoint') ||
        process.env.REACT_APP_GRAPHQL_ENDPOINT,
      region: getEnvConfigByKey('region') || process.env.REACT_APP_REGION || '',
      isNotificationEnabled:
        getEnvConfigByKey('notificationGraphqlEndpoint') ||
        process.env.REACT_APP_NOTIFICATION_GRAPHQL_ENDPOINT,
      notificationUrl:
        getEnvConfigByKey('notificationGraphqlEndpoint') ||
        process.env.REACT_APP_NOTIFICATION_GRAPHQL_ENDPOINT,
      notificationRegion:
        getEnvConfigByKey('notificationRegion') ||
        process.env.REACT_APP_REGION ||
        '',
    };
  }
}

// Legacy Query / Mutation Functions
interface makeQueryOptions<T> extends QueryOptions<T> {
  clientName?: string;
}
export const makeQuery = async <TData = any, TVariables = OperationVariables>(
  data: makeQueryOptions<TVariables>
) => {
  const { clientName = BACKEND_REQUEST, ...rest } = data;
  const client = AppSyncClient.getClient();

  return client.query<TData, any>({
    context: { clientName },
    fetchPolicy: 'network-only',
    ...rest,
  });
};

interface makeMutationOptions extends MutationOptions {
  clientName?: string;
}
export const makeMutation = async (data: makeMutationOptions): Promise<any> => {
  const { clientName = BACKEND_REQUEST, ...rest } = data;
  if (clientName === NOTIFICATION_REQUEST && Auth.isImpersonating()) {
    return;
  }
  const client = AppSyncClient.getClient();
  return client.mutate({
    context: { clientName },
    ...rest,
  });
};

const QUERY_CACHE_TTL = 1000 * 60 * 30; // 30 min

export const usePeriodicCacheReset = () => {
  const client = useApolloClient();

  useEffect(() => {
    const clearCacheIntervalId = setInterval(() => {
      client?.cache.reset();
    }, QUERY_CACHE_TTL);

    return () => clearInterval(clearCacheIntervalId);
  }, [client]);
};

export default AppSyncClient;
